import {
  FormMessage,
  FormMessageVariant,
  Icon,
  IconIdentifier,
} from '@aus-platform/design-system';
import classNames from 'classnames';
import { isNil } from 'lodash';
import React, { useContext, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { GlobalContext } from 'shared/context';
import { FeatureFlag } from 'shared/enums';
import { isOrgAdmin } from 'shared/helpers';
import { SSO_ENFORCEMENT_MESSAGES } from '../constants';
import { SettingsTab } from '../enums';
import { OrgControlsTab } from '../org-controls-tab';
import { ProfileTab } from '../profile-tab';
import { SecurityTab } from '../security-tab';
import type { SettingsModalProps, TabConfig } from '../types';

const tabs: TabConfig[] = [
  {
    key: SettingsTab.Profile,
    label: 'Profile',
    icon: IconIdentifier.Person,
  },
  {
    key: SettingsTab.Security,
    label: 'Security',
    icon: IconIdentifier.Auth,
    featureFlag: FeatureFlag.TwoFactorAuthentication,
  },
  {
    key: SettingsTab.OrgControls,
    label: 'Org Controls',
    icon: IconIdentifier.GearFill,
  },
];

export const SettingsModal: React.FC<SettingsModalProps> = ({
  show,
  onClose,
}) => {
  // Contexts.
  const { loggedUser } = useContext(GlobalContext);

  // States.
  const [activeTab, setActiveTab] = useState<SettingsTab>(SettingsTab.Profile);
  const [unsavedChangesTab, setUnsavedChangesTab] =
    useState<SettingsTab | null>(null);
  const [showUnsavedBanner, setShowUnsavedBanner] = useState(false);

  // Handlers.
  const handleClose = () => {
    if (unsavedChangesTab) {
      setShowUnsavedBanner(true);
      return;
    }
    setActiveTab(SettingsTab.Profile);
    onClose();
  };

  const handleUnsavedChanges = (hasChanges: boolean) => {
    setUnsavedChangesTab(hasChanges ? activeTab : null);
    if (!hasChanges) {
      setShowUnsavedBanner(false);
    }
  };

  const handleTabClick = (tabKey: SettingsTab) => {
    if (showUnsavedBanner && tabKey !== unsavedChangesTab) {
      return;
    }
    if (unsavedChangesTab && tabKey !== unsavedChangesTab) {
      setShowUnsavedBanner(true);
      return;
    }
    setShowUnsavedBanner(false);
    setActiveTab(tabKey);
  };

  const isTabVisible = (tab: TabConfig) => {
    if (tab.key === SettingsTab.OrgControls) {
      return isOrgAdmin(loggedUser);
    }

    if (!isNil(tab.featureFlag)) {
      return loggedUser?.featureFlags[tab.featureFlag];
    }

    return true;
  };

  const getTabClassName = (tabKey: SettingsTab) => {
    return classNames('settings-modal__sidebar__item', {
      'settings-modal__sidebar__item--active': activeTab === tabKey,
      'settings-modal__sidebar__item--disabled':
        showUnsavedBanner && tabKey !== unsavedChangesTab,
    });
  };

  // Renderers.
  const renderTab = (tab: TabConfig) => {
    return (
      <div
        key={tab.key}
        className={getTabClassName(tab.key)}
        onClick={() => handleTabClick(tab.key)}
      >
        <Icon identifier={tab.icon} size={16} />
        {tab.label}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case SettingsTab.Profile:
        return <ProfileTab onClose={handleClose} />;
      case SettingsTab.Security:
        return <SecurityTab />;
      case SettingsTab.OrgControls:
        return (
          <OrgControlsTab
            onUnsavedChanges={handleUnsavedChanges}
            onShowUnsavedBanner={() => setShowUnsavedBanner(true)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="settings-modal"
      keyboard={false}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Settings</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="settings-modal__sidebar">
          {tabs.filter(isTabVisible).map(renderTab)}
        </div>
        <div className="settings-modal__content">
          {showUnsavedBanner && (
            <FormMessage
              showIcon
              iconIdentifier={IconIdentifier.InfoCircle}
              variant={FormMessageVariant.Warning}
              message={SSO_ENFORCEMENT_MESSAGES.UnsavedChangesWarning}
              className="settings-modal__unsaved-banner"
            />
          )}
          {renderContent()}
        </div>
      </Modal.Body>
    </Modal>
  );
};
